import React, {useMemo} from "react";
import {Form} from "react-bootstrap";

const SCOPE_LABELS = {
  SELF: "Własne dane",
  MANAGED_DEPARTMENTS: "Zarządzane działy",
  SELECTED_DEPARTMENTS: "Wybrane działy",
  ALL: "Cała firma"
};
const SCOPES = Object.keys(SCOPE_LABELS);

const PermissionMatrix = ({catalog, grants, onChange, mode = "role", disabled = false}) => {
  const grouped = useMemo(() => (catalog || []).reduce((acc, permission) => {
    const group = permission.group || "Pozostałe";
    if (!acc[group]) acc[group] = [];
    acc[group].push(permission);
    return acc;
  }, {}), [catalog]);

  const grantMap = useMemo(() => new Map((grants || []).map(item => [item.code, item])), [grants]);

  const updateRole = (permission, enabled, scope) => {
    const next = (grants || []).filter(item => item.code !== permission.code);
    if (enabled) {
      const resolvedScope = permission.scoped ? (scope || "ALL") : "ALL";
      next.push({
        code: permission.code,
        scope: resolvedScope,
        departmentIds: grantMap.get(permission.code)?.departmentIds || []
      });
    }
    onChange(next);
  };

  const updateUser = (permission, effect, scope) => {
    const next = (grants || []).filter(item => item.code !== permission.code);
    if (effect !== "INHERIT") {
      const resolvedScope = effect === "ALLOW" ? (permission.scoped ? (scope || "ALL") : "ALL") : null;
      next.push({
        code: permission.code,
        effect,
        scope: resolvedScope,
        departmentIds: effect === "ALLOW" ? (grantMap.get(permission.code)?.departmentIds || []) : []
      });
    }
    onChange(next);
  };

  const updateDepartments = (code, value) => {
    const ids = value
      .split(",")
      .map(item => Number(item.trim()))
      .filter(item => Number.isInteger(item) && item > 0);
    const current = grantMap.get(code);
    if (!current) return;
    const next = (grants || []).filter(item => item.code !== code);
    next.push({...current, departmentIds: [...new Set(ids)]});
    onChange(next);
  };

  return (
    <div className={`qstaff-permission-matrix qstaff-permission-matrix-${mode}`}>
      {Object.entries(grouped).map(([group, permissions]) => {
        const enabledCount = permissions.filter(permission => grantMap.has(permission.code)).length;
        return (
          <section key={group} className="qstaff-permission-group">
            <div className="qstaff-permission-group-header">
              <h6>{group}</h6>
              {mode === "role" && <span>{enabledCount}/{permissions.length}</span>}
            </div>

            <div className="qstaff-permission-items">
              {permissions.map(permission => {
                const grant = grantMap.get(permission.code);
                const inputId = `permission-${mode}-${permission.code}`;

                if (mode === "role") {
                  return (
                    <div key={permission.code} className={`qstaff-permission-item ${grant ? "enabled" : ""}`} title={permission.description || permission.label}>
                      <div className="qstaff-permission-check">
                        <input
                          id={inputId}
                          className="form-check-input qstaff-permission-checkbox"
                          type="checkbox"
                          checked={!!grant}
                          disabled={disabled}
                          onChange={event => updateRole(permission, event.target.checked, grant?.scope)}
                        />
                        <label htmlFor={inputId}>{permission.label}</label>
                      </div>

                      {permission.scoped && grant && (
                        <Form.Select
                          size="sm"
                          className="qstaff-permission-scope"
                          value={grant.scope || "ALL"}
                          disabled={disabled}
                          onChange={event => updateRole(permission, true, event.target.value)}
                        >
                          {SCOPES.map(scope => <option key={scope} value={scope}>{SCOPE_LABELS[scope]}</option>)}
                        </Form.Select>
                      )}

                      {permission.scoped && grant?.scope === "SELECTED_DEPARTMENTS" && (
                        <Form.Control
                          size="sm"
                          className="qstaff-permission-departments"
                          value={(grant.departmentIds || []).join(",")}
                          disabled={disabled}
                          onChange={event => updateDepartments(permission.code, event.target.value)}
                          placeholder="ID działów: 1,2,3"
                        />
                      )}
                    </div>
                  );
                }

                return (
                  <div key={permission.code} className="qstaff-permission-item qstaff-permission-item-user" title={permission.description || permission.label}>
                    <div className="qstaff-permission-user-label">{permission.label}</div>
                    <Form.Select
                      size="sm"
                      value={grant?.effect || "INHERIT"}
                      disabled={disabled}
                      onChange={event => updateUser(permission, event.target.value, grant?.scope)}
                    >
                      <option value="INHERIT">Dziedzicz</option>
                      <option value="ALLOW">Nadaj</option>
                      <option value="DENY">Odbierz</option>
                    </Form.Select>
                    {permission.scoped && grant?.effect === "ALLOW" && (
                      <Form.Select
                        size="sm"
                        value={grant?.scope || "ALL"}
                        disabled={disabled}
                        onChange={event => updateUser(permission, "ALLOW", event.target.value)}
                      >
                        {SCOPES.map(scope => <option key={scope} value={scope}>{SCOPE_LABELS[scope]}</option>)}
                      </Form.Select>
                    )}
                    {permission.scoped && grant?.effect === "ALLOW" && grant?.scope === "SELECTED_DEPARTMENTS" && (
                      <Form.Control
                        size="sm"
                        value={(grant?.departmentIds || []).join(",")}
                        disabled={disabled}
                        onChange={event => updateDepartments(permission.code, event.target.value)}
                        placeholder="ID działów: 1,2,3"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default PermissionMatrix;
